import 'react';
import classnames from 'classnames';
import { formatCurrency } from '@thisisbud/intl-utils';
import { AccessibleContent } from '@thisisbud/bui-react-accessible-content';
import { jsx } from 'react/jsx-runtime';

const formattedCurrencyClassName = 'bui-formatted-currency';
function defaultFormatCurrencyWrapper(amount, currency) {
  return formatCurrency('en-GB', amount, currency);
}

/**
 * Renders a currency in a localized format with an alternative version for screen readers.
 * By default the currency is rendered with the default currency format from `@thisisbud/intl-formats` and for the `en-GB` locale, but this can be modified by providing a custom currency formatting function via the `formatCurrency` prop.
 *
 * @param props - The component props
 * @public
 *
 * @example
 * ```javascript
 * import * as React from 'react'
 * import { FormattedCurrency } from '@thisisbud/bui-react-formatted-currency'
 * import { useI18n } from '@thisisbud/i18n-react'
 *
 * const currency = 'EUR'
 * const amount = 1.25
 *
 * function Example() {
 *   const i18n = useI18n()
 *
 *   return <FormattedCurrency amount={amount} currency={currency} formatCurrency={i18n.formatCurrency}/>
 * }
 * ```
 */
function FormattedCurrency(props) {
  const {
    amount,
    className,
    currency,
    formatAccessibleCurrency,
    formatCurrency
  } = props;
  const formatCurrencyFn = typeof formatCurrency === 'function' ? formatCurrency : defaultFormatCurrencyWrapper;
  const formatAccessibleCurrencyFn = typeof formatAccessibleCurrency === 'function' ? formatAccessibleCurrency : formatCurrencyFn;
  return /*#__PURE__*/jsx(AccessibleContent, {
    accessibleAlternative: formatAccessibleCurrencyFn(amount, currency),
    className: classnames(formattedCurrencyClassName, className),
    children: formatCurrencyFn(amount, currency)
  });
}
FormattedCurrency.displayName = 'FormattedCurrency';

export { FormattedCurrency, formattedCurrencyClassName };
